/**
 * One chair.
 *
 * Position and facing are handed in from the shared geometry module — this
 * component never computes or remembers where it sits. Changing the chair count
 * re-derives the whole ring and framer-motion springs each chair from its old
 * derived position to its new one, so the ring reflows instead of jumping.
 */

import { motion } from 'framer-motion'
import { memo, useRef } from 'react'

import { type Swatch, avatarTint, initialsOf } from '../lib/colors'
import { beginGuestDrag } from '../lib/dragGuest'
import { nextRsvp } from '../lib/guests'
import type { Catalogue } from '../lib/i18n'
import { useT } from '../lib/i18n/I18nProvider'
import { finishPlacing } from '../lib/placing'
import {
  SEAT_PLATE_LENGTH,
  SEAT_PLATE_WIDTH,
  SEAT_RADIUS,
  type SeatPoint,
} from '../lib/geometry'
import type { Guest, Rsvp, Seat, SeatLabelMode, Table } from '../lib/types'
import { usePlanStore } from '../store/planStore'
import { tapsSuppressed, useViewStore } from '../store/viewStore'

interface Props {
  seat: Seat
  point: SeatPoint
  table: Table
  guest: Guest | null
  swatch: Swatch
  showLabel: boolean
  /** Mirrors the PDF: only `name` is drawn as an outward plate. */
  labels: SeatLabelMode
  spring: object
}

/**
 * The answer in the plan's own words, and how loud to say it.
 *
 * The same three words the guest panel's segmented control uses, so the toast
 * names what the planner just watched that control move to.
 */
function rsvpWord(t: Catalogue, rsvp: Rsvp): string {
  if (rsvp === 'yes') return t.inspector.rsvpComing
  if (rsvp === 'no') return t.inspector.rsvpCant
  return t.inspector.rsvpPending
}

const RSVP_TONE = { yes: 'success', no: 'warn', pending: 'info' } as const

/**
 * How far to turn a plate so its long axis points outward, without ever
 * standing the name on its head.
 *
 * CSS rotates clockwise with y pointing down, so the outward direction for
 * `facing` sits at `facing - 90`. Half the ring would then read upside down, so
 * anything outside [-90, 90) is turned through 180 — which also swaps which end
 * of the plate the first line belongs at. Same rule as the PDF's
 * `_upright_angle`, so the screen and the print agree.
 */
function plateAngle(facing: number): { angle: number; flipped: boolean } {
  let angle = facing - 90
  let flipped = false
  while (angle < -90) {
    angle += 180
    flipped = !flipped
  }
  while (angle >= 90) {
    angle -= 180
    flipped = !flipped
  }
  return { angle, flipped }
}

function SeatNodeImpl({ seat, point, table, guest, swatch, showLabel, labels, spring }: Props) {
  const t = useT()
  // Booleans, not the objects: 1,224 chairs subscribe, and hovering during a
  // guest drag must re-render the two chairs it concerns, not all of them.
  const isActive = usePlanStore((s) => s.activeSeat?.seatId === seat.id)
  const isDropTarget = useViewStore((s) => s.drag !== null && s.hoverSeat?.seatId === seat.id)
  const isDraggedFrom = useViewStore((s) => s.drag?.from?.seatId === seat.id)
  // Which kind of pointer pressed this chair last, so a double-click can tell
  // a mouse from a finger's double-tap. A ref, not state: it must not render.
  const pointerTypeRef = useRef('mouse')

  /**
   * A click opens the chair — in the side panel, or the sheet on the compact
   * editor — unless someone is waiting to be seated, whom this chair now gets.
   */
  const openSeat = (event: React.MouseEvent) => {
    event.stopPropagation()
    const view = useViewStore.getState()
    if (tapsSuppressed(view)) return
    const ref = { tableId: table.id, seatId: seat.id }
    if (view.placing) {
      finishPlacing(ref, t)
      return
    }
    usePlanStore.getState().setActiveSeat(ref)
    if (view.compact) view.openPanel('selection')
  }

  /**
   * Dragging a seated guest moves them; a plain click opens the editor. The
   * threshold in beginGuestDrag is what keeps those two apart — otherwise a
   * click would register as a drag that ends on its own chair.
   */
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation()
    pointerTypeRef.current = event.pointerType
    if (!guest) return
    beginGuestDrag(event, guest.id, { tableId: table.id, seatId: seat.id })
  }

  /**
   * A double-click walks the guest's answer round the ring: coming, cannot
   * come, no answer yet. Marking a table as the replies arrive is otherwise a
   * trip to the panel and back for every chair.
   *
   * A finger is not offered this. On a compact layout the first tap opens the
   * sheet and `revealSelection` glides the tapped table clear of it, so the
   * chair has moved out from under the finger before a second tap could land —
   * and the sheet that opened carries the same three buttons. The floor's
   * double-click turns touch away for its own version of the reason.
   */
  const cycleRsvp = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!guest || pointerTypeRef.current !== 'mouse') return
    const view = useViewStore.getState()
    // A guest waiting for a chair owns the next click on one, and a pinch's
    // lifting fingers produce clicks of their own.
    if (view.placing || tapsSuppressed(view)) return
    const rsvp = nextRsvp(guest.rsvp)
    const store = usePlanStore.getState()
    store.updateGuest(guest.id, { rsvp })
    store.showToast(`${guest.fullName} — ${rsvpWord(t, rsvp)}`, RSVP_TONE[rsvp])
  }

  const label = guest ? guest.fullName.split(' ')[0] : ''

  if (labels === 'name') {
    const { angle, flipped } = plateAngle(point.facing)
    const words = guest ? guest.fullName.trim().split(/\s+/) : []
    const lines = words.length > 1 ? [words[0], words.slice(1).join(' ')] : words
    return (
      <motion.div
        className={[
          'seat seat--plate',
          guest ? 'seat--taken' : 'seat--empty',
          guest?.rsvp === 'no' ? 'seat--declined' : '',
          isActive ? 'is-active' : '',
          isDropTarget ? 'is-drop-target' : '',
          isDraggedFrom ? 'is-lifted' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        initial={false}
        animate={{ x: point.x, y: point.y }}
        transition={spring}
        data-seat-id={seat.id}
        data-table-id={table.id}
        onPointerDown={onPointerDown}
        onClick={openSeat}
        onDoubleClick={cycleRsvp}
        title={guest ? `${guest.fullName} — seat ${seat.position + 1}` : `Seat ${seat.position + 1}`}
      >
        <span
          className="seat-plate"
          style={{
            width: SEAT_PLATE_LENGTH,
            height: SEAT_PLATE_WIDTH,
            left: -SEAT_PLATE_LENGTH / 2,
            top: -SEAT_PLATE_WIDTH / 2,
            // Turn about its own centre, then push out along that direction so
            // the plate's inner edge lands on the chair's inner edge — the same
            // offset `seatPlate()` derives. `plateAngle` may have turned the
            // plate 180° so the name reads upright; the push must still go
            // *outward*, so its sign flips with it.
            transform: `rotate(${angle}deg) translateX(${
              (flipped ? -1 : 1) * (SEAT_PLATE_LENGTH / 2 - SEAT_RADIUS)
            }px)`,
            background: guest ? swatch.accent : swatch.seat,
            borderColor: guest ? swatch.accent : swatch.seatEdge,
            color: guest ? '#FBFAF8' : swatch.seatEdge,
            flexDirection: flipped ? 'column-reverse' : 'column',
          }}
        >
          {/* Index keys: both lines of "Li Li" are the word "Li", and equal
              string keys would collide. The list is tiny and rebuilt per render.
              Below the label zoom the name would be under 5px on screen — the
              plate keeps its colour and drops the text, which on a big wedding
              is most of what each frame costs to composite. */}
          {showLabel &&
            lines.map((line, index) => (
              <span key={index} className="seat-plate-line">
                {line}
              </span>
            ))}
        </span>
      </motion.div>
    )
  }

  return (
    <motion.div
      className={[
        'seat',
        guest ? 'seat--taken' : 'seat--empty',
        guest?.rsvp === 'no' ? 'seat--declined' : '',
        isActive ? 'is-active' : '',
        isDropTarget ? 'is-drop-target' : '',
        isDraggedFrom ? 'is-lifted' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // Chairs are laid out purely with transforms so the browser can animate
      // them on the compositor; margins do the centring that translate(-50%)
      // would otherwise occupy in the transform.
      style={{
        marginLeft: -SEAT_RADIUS,
        marginTop: -SEAT_RADIUS,
        width: SEAT_RADIUS * 2,
        height: SEAT_RADIUS * 2,
      }}
      initial={false}
      animate={{ x: point.x, y: point.y }}
      transition={spring}
      data-seat-id={seat.id}
      data-table-id={table.id}
      onPointerDown={onPointerDown}
      onClick={openSeat}
      onDoubleClick={cycleRsvp}
      title={guest ? `${guest.fullName} — seat ${seat.position + 1}` : `Seat ${seat.position + 1}`}
    >
      {/* Backrest marks which way the chair faces. Rotate to the facing angle,
          then push outward along it — facing 0 is "up", which is -Y. */}
      <span
        className="seat-back"
        style={{
          transform: `rotate(${point.facing}deg) translateY(-${SEAT_RADIUS + 5}px)`,
          background: swatch.accent,
        }}
      />

      <span
        className="seat-disc"
        style={{
          background: guest ? avatarTint(guest.id) : swatch.seat,
          borderColor: guest ? swatch.accent : swatch.seatEdge,
        }}
      >
        {guest?.photoUrl ? (
          <img src={guest.photoUrl} alt="" className="seat-photo" draggable={false} />
        ) : guest ? (
          <span className="seat-initials">{initialsOf(guest.fullName)}</span>
        ) : (
          <span className="seat-number" style={{ color: swatch.accent }}>
            {seat.position + 1}
          </span>
        )}
      </span>

      {showLabel && label && (
        <span
          className="seat-label"
          // Push the label out past the chair along the facing direction, then
          // counter-rotate so the text itself stays upright and readable.
          style={{
            transform: `rotate(${point.facing}deg) translateY(-${SEAT_RADIUS + 15}px) rotate(${-point.facing}deg)`,
          }}
        >
          {label}
        </span>
      )}
    </motion.div>
  )
}

export const SeatNode = memo(SeatNodeImpl)
